import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { ChampionshipDetail, ChampionshipStatus } from "@/types";
import { CHAMPIONSHIP_STATUS_LABEL, apiError, championshipTone, personName } from "@/lib/championships";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { ManagersSection } from "@/components/championships/ManagersSection";
import { TeamsSection } from "@/components/championships/TeamsSection";
import { MatchesSection } from "@/components/championships/MatchesSection";

const selectClass =
  "w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

// Ficha de un campeonato. Solo trae la cabecera y los contadores: gestores, parejas y partidos son
// listados paginados con sus propios filtros (con cientos de filas, cargarlo todo de golpe no escala).
export function ChampionshipDetailPage() {
  const { id } = useParams();
  const campeonatoId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: campeonato, isLoading, isError } = useQuery({
    queryKey: ["championship", campeonatoId, "detail"],
    queryFn: async () => (await api.get<ChampionshipDetail>(`/championships/${campeonatoId}`)).data,
    retry: false,
  });

  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [estado, setEstado] = useState<ChampionshipStatus>("BORRADOR");
  useEffect(() => {
    if (!campeonato) return;
    setNombre(campeonato.nombre);
    setDescripcion(campeonato.descripcion ?? "");
    setEstado(campeonato.estado);
  }, [campeonato]);

  const saveInfo = useMutation({
    mutationFn: async () =>
      (await api.put<ChampionshipDetail>(`/championships/${campeonatoId}`, { nombre: nombre.trim(), descripcion: descripcion.trim() || null, estado })).data,
    onSuccess: (updated) => {
      queryClient.setQueryData(["championship", campeonatoId, "detail"], updated);
      queryClient.invalidateQueries({ queryKey: ["championships"] });
      toast.success("Campeonato actualizado");
    },
    onError: (error) => toast.error(apiError(error, "No se pudo actualizar el campeonato")),
  });

  const [confirmSaveInfo, setConfirmSaveInfo] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteChampionship = useMutation({
    mutationFn: () => api.delete(`/championships/${campeonatoId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["championships"] });
      toast.success("Campeonato eliminado");
      navigate("/campeonatos");
    },
    onError: (error) => toast.error(apiError(error, "No se pudo eliminar el campeonato")),
  });

  if (isLoading) return <p className="text-sm text-ink-400">Cargando campeonato…</p>;
  if (isError || !campeonato) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs items={[{ label: "Campeonatos", to: "/campeonatos" }, { label: "No disponible" }]} />
        <Card>
          <p className="text-sm text-ink-500">Este campeonato no existe o no tienes acceso a él.</p>
        </Card>
      </div>
    );
  }

  const { canManage, isOwner } = campeonato;
  const infoChanged =
    nombre.trim() !== campeonato.nombre || (descripcion.trim() || null) !== (campeonato.descripcion ?? null) || estado !== campeonato.estado;

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Campeonatos", to: "/campeonatos" }, { label: campeonato.nombre }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <Trophy className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold break-words text-ink-900">{campeonato.nombre}</h1>
            <p className="text-ink-400">
              Organiza: {campeonato.provider ? personName(campeonato.provider) : "Plataforma"} · {campeonato.equiposCount} pareja
              {campeonato.equiposCount === 1 ? "" : "s"} · {campeonato.partidosCount} partido{campeonato.partidosCount === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={championshipTone(campeonato.estado)}>{CHAMPIONSHIP_STATUS_LABEL[campeonato.estado]}</Badge>
          {canManage ? <Badge tone="green">Puedes gestionarlo</Badge> : <Badge tone="slate">Solo lectura</Badge>}
        </div>
      </div>

      {canManage ? (
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setConfirmSaveInfo(true);
            }}
            className="flex flex-col gap-4"
          >
            <p className="text-sm font-medium text-ink-600">Datos del campeonato</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Input id="camp-nombre" label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} className="w-full" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="camp-estado" className="text-sm font-medium text-ink-600">
                  Estado
                </label>
                <select id="camp-estado" value={estado} onChange={(e) => setEstado(e.target.value as ChampionshipStatus)} className={selectClass}>
                  {(Object.keys(CHAMPIONSHIP_STATUS_LABEL) as ChampionshipStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {CHAMPIONSHIP_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="camp-desc" className="text-sm font-medium text-ink-600">
                Descripción
              </label>
              <textarea
                id="camp-desc"
                rows={3}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {isOwner && (
                <Button type="button" variant="danger" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4" />
                  Eliminar campeonato
                </Button>
              )}
              <Button type="submit" disabled={saveInfo.isPending || !infoChanged || nombre.trim().length < 2}>
                Guardar cambios
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        campeonato.descripcion && (
          <Card>
            <p className="text-sm break-words text-ink-600">{campeonato.descripcion}</p>
          </Card>
        )
      )}

      {/* Gestores: solo el organizador, justo encima de las parejas */}
      {isOwner && <ManagersSection campeonato={campeonato} />}

      <TeamsSection campeonato={campeonato} />
      <MatchesSection campeonato={campeonato} />

      <ConfirmDialog
        open={confirmSaveInfo}
        onOpenChange={setConfirmSaveInfo}
        title="Guardar cambios del campeonato"
        description={`¿Seguro que quieres guardar los cambios de "${campeonato.nombre}"?`}
        tone="primary"
        confirmLabel="Guardar"
        onConfirm={() => saveInfo.mutate()}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Eliminar campeonato"
        description={`Se eliminará "${campeonato.nombre}" con todas sus parejas y partidos, y se liberarán las pistas reservadas que aún no hayan empezado. Esta acción no se puede deshacer.`}
        tone="danger"
        confirmLabel="Eliminar"
        onConfirm={() => deleteChampionship.mutate()}
      />
    </div>
  );
}
